import { useEffect, useState } from 'react';
import { useSWRConfig } from 'swr';
import { setSessionStorageItem } from '@/lib/browserStorage';

import { enqueueSnackbar } from '@/providers/SnackbarProvider';
import { usePrivate } from './PrivateContext';
import { submitSurveyMessages } from './messages';
import { SNACKBAR_VARIANT } from '@/constants/notifications';
import { logError } from '@/lib/logger';

export function useSurveyFlow() {
  const { mutate } = useSWRConfig();

  const { userId, userProfile, userProfileApi } = usePrivate();

  const [surveyDialogOpen, setSurveyDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleClickSkipQuestion = () => {
    setSessionStorageItem<boolean>('surveySkipped', true);
    setSurveyDialogOpen(false);
  };

  const handleClickSubmitSurvey = (surveyResponse: string[]) => {
    setSessionStorageItem<boolean>('surveySkipped', true);
    if (userId && userProfile && userProfileApi && !loading) {
      setLoading(true);
      userProfileApi
        .apiUserProfileSubmitSurveyPatch({
          userId,
          surveyResponseData: surveyResponse.join(','),
        })
        .then(() => {
          setSurveyDialogOpen(false);
          mutate(`/api/Animal/AnimalGroup/${userId}`);
        })
        .catch((error) => {
          enqueueSnackbar(submitSurveyMessages['submitSurvey.unexpctedError'], {
            variant: SNACKBAR_VARIANT.ERROR,
          });
          logError(
            '[BREEDLOGIC] useSurveyFlow:handleClickSubmitSurvey - Error submitting survey',
            error,
          );
        })
        .finally(() => setLoading(false));
    }
  };

  useEffect(() => {
    if (userProfile && !userProfile.surveyResponse?.surveyResponseEntityId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- opens the survey dialog once the async userProfile loads without a survey response; the dialog is otherwise toggled by user actions (data-fetch → state)
      setSurveyDialogOpen(true);
    }
  }, [userProfile]);

  return {
    isSubmittingSurvey: loading,
    userProfile,
    surveyDialogOpen,
    onClickSkipQuestion: handleClickSkipQuestion,
    onClickSubmitSurvey: handleClickSubmitSurvey,
  };
}
